// ============= Imports =============
import { AuthContextProps, tokenManager } from '@tw/tw-component-library';
import { ApiResponse } from '../types';

// ============= Types =============
interface GetDocumentParams {
  documentId: string;
  getAccessToken: AuthContextProps['getAccessToken'];
}

interface UploadResponse {
  success: boolean;
  message: string;
  data?: any;
}

// ============= Constants =============
const IS_DEVELOPMENT = import.meta.env.MODE === 'development';
const PROD_BASE_URL = import.meta.env.VITE_REQUEST_URL;
const API_ENDPOINT = '/api/RemoveDocument';
const SIDETRADE_SCOPE =
  'api://b922470e-04e2-4c04-84fc-e5ec591459ad/SidetradeFrontEndProd';
const HEADERS = {};

// ============= Utility Functions =============
const constructUploadUrl = (documentId: string): string => {
  if (IS_DEVELOPMENT) {
    // In development, use relative URL for proxy
    return `${API_ENDPOINT}?&documentId=${encodeURIComponent(documentId)}`;
  } else {
    // In production, use full URL
    return `${PROD_BASE_URL}${API_ENDPOINT}?&documentId=${encodeURIComponent(
      documentId
    )}`;
  }
};

/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Upload Function =============
export const deleteDocument = async ({
  documentId,
  getAccessToken,
}: GetDocumentParams): Promise<ApiResponse<UploadResponse>> => {
  try {
    console.log(
      `🔄 Uploading document in ${
        IS_DEVELOPMENT ? 'development' : 'production'
      } mode...`
    );
    const url = constructUploadUrl(documentId);
    console.log('🌐 Upload URL:', url);

    // Token acquisition with required scopes

    const token = await getAccessToken([SIDETRADE_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire Graph API token');
    }
    tokenManager.logTokenInfo(token, 'Graph API');

    const fetchOptions: RequestInit = {
      method: 'DELETE',
      headers: createHeaders(token),
      ...(IS_DEVELOPMENT && { credentials: 'include' }),
    };

    const response = await fetch(url, fetchOptions);
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Upload failed:', response.status, errorText);
      throw new Error(`Upload failed: ${response.status} ${errorText}`);
    }

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      // Handle text response
      const text = await response.text();
      data = { message: text };
    }

    console.log('✅ Document Deleted successfully');

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('🔴 Error uploading document:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed',
    };
  }
};
